/**
 * Determines if the provisional primary diagnosis is set to ADHD after the Clinical Interview based on the patient data
 * @param {Object} patientDataModel - The patient data model
 * @returns {boolean} - True if the provisional primary diagnosis is set to ADHD, false otherwise
 */
export const isADHDProvisionalPrimaryDiagnosis = (patientDataModel) => {
  // Early return if no data model
  if (!patientDataModel?.dataModel) return true;

  const CLINICAL_INTERVIEW_MODULE = '2_clinicalInterview';
  const DIAGNOSTIC_FORMULATION_VAR_NAME = 'diagnosticFormulation';
  const ADHD_CONDITION_NAME = 'Attention Deficit Hyperactivity Disorder (ADHD)';
  
  try {
      // Find the Diagnostic Formulation section by its sectionVarName
      const diagnosticFormulationSection = Object.values(patientDataModel?.dataModel?.[CLINICAL_INTERVIEW_MODULE]?.sections || {})
          .find(section => section?.sectionVarName === DIAGNOSTIC_FORMULATION_VAR_NAME);
      
      // Get the provisional primary diagnosis from the found section
      const provisionalPrimaryDiagnosis = diagnosticFormulationSection?.provisionalPrimaryDiagnosis;
      
      // Return true if no diagnosis is set or if it's ADHD (case-insensitive)
      return !provisionalPrimaryDiagnosis || provisionalPrimaryDiagnosis.toLowerCase() === ADHD_CONDITION_NAME.toLowerCase();
  } catch (error) {
      console.error('Error checking ADHD flow condition:', error);
      return true; // Default to ADHD flow on error
  }
};

const POSITIVE_DIAGNOSIS_DECISIONS = ['Fully met', 'Partially met'];

/**
 * Formal ADHD diagnostic outcome from the Diagnostic Decision module only.
 * Do not use for PAD content when a SLaM Path-1 provisional outcome exists —
 * use resolvePadAdhdOutcome / isPadAdhdOutcomePositive instead.
 *
 * @param {Object} patientDataModel - The patient data model
 * @returns {boolean} - True if ADHD diagnosis is fully or partially met, false otherwise
 */
export const isADHDDiagnosisPositive = (patientDataModel) => {
  // Early return if no data model
  if (!patientDataModel?.dataModel) return false;

  const DIAGNOSTIC_DECISION_MODULE = '4_diagnosticDecision';
  const DIAGNOSTIC_DECISION_VAR_NAME = 'diagnosticDecision';

  try {
      // Find the Diagnostic Decision section by its sectionVarName
      const diagnosticDecisionSection = Object.values(patientDataModel?.dataModel?.[DIAGNOSTIC_DECISION_MODULE]?.sections || {})
          .find(section => section?.sectionVarName === DIAGNOSTIC_DECISION_VAR_NAME);

      if (!diagnosticDecisionSection) return false;

      // Get the diagnosis decision from the found section
      const diagnosisDecision = diagnosticDecisionSection?.diagnosisDecision;

      // Return true if diagnosis is fully or partially met
      return POSITIVE_DIAGNOSIS_DECISIONS.includes(diagnosisDecision);
  } catch (error) {
      console.error('Error checking ADHD diagnosis:', error);
      return false; // Default to false on error
  }
};

/**
 * Reads the SLaM / 3 Borough Path-1 provisional PAD outcome, but ONLY when it is
 * actually in force — i.e. the Lead chose to run the Post-Assessment Discussion
 * in the same appointment (`ciFinishPath === 'padInAppointment'`) and recorded a
 * valid positive/negative choice. Returns null otherwise so callers fall back to
 * the formal Diagnostic Decision. Single source of truth for reading this pair
 * of fields so no other module has to re-implement the same lookup.
 *
 * @param {Object|null} patientDataModel
 * @returns {'positive'|'negative'|null}
 */
export const getPadProvisionalOutcome = (patientDataModel) => {
  const ciFinishPath = patientDataModel?.workflowDecisions?.ciFinishPath;
  const provisional =
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.provisionalAdhdOutcome;

  if (
    ciFinishPath === 'padInAppointment' &&
    (provisional === 'positive' || provisional === 'negative')
  ) {
    return provisional;
  }
  return null;
};

/**
 * CONTENT resolver — the effective ADHD outcome for the *live PAD experience*:
 * the PAD sidebar/section unlock, `ShouldDisplayElement` panel gating, and the
 * text the PAD LLM drafts.
 *
 * SLaM / 3 Borough Path 1 (padInAppointment): the clinician chose a provisional
 * positive/negative PAD *before* recording the formal DD. Under the v1
 * soft-warning regime the drafted PAD narrative "freezes" to what was actually
 * discussed with the patient, so this resolver keeps returning that provisional
 * choice even after a (possibly differing) DD is later recorded — the PAD is
 * never auto-switched. Path 2 / legacy / everyone else: fall through to formal DD.
 *
 * Contrast with resolvePadReportStructureOutcome (below), which lets the formal
 * DD win once it exists. Before DD is recorded the two resolvers always agree
 * (DD absent -> provisional); they only diverge on a genuine DD-vs-PAD mismatch.
 *
 * @param {Object|null} patientDataModel
 * @returns {'positive'|'negative'}
 */
export const resolvePadAdhdOutcome = (patientDataModel) => {
  const provisional = getPadProvisionalOutcome(patientDataModel);
  if (provisional) return provisional;
  return isADHDDiagnosisPositive(patientDataModel) ? 'positive' : 'negative';
};

/**
 * Boolean convenience for PAD pages that previously called isADHDDiagnosisPositive.
 * Uses the CONTENT resolver (provisional-preferred). See resolvePadAdhdOutcome.
 *
 * @param {Object|null} patientDataModel
 * @returns {boolean}
 */
export const isPadAdhdOutcomePositive = (patientDataModel) =>
  resolvePadAdhdOutcome(patientDataModel) === 'positive';

/**
 * REPORT-STRUCTURE resolver — the effective ADHD outcome for the *rendered
 * Clinical Assessment Report (CAR) PAD section* conditional structure
 * (ADHD_POSITIVE blocks, goals placeholder, etc.).
 *
 * The formal Diagnostic Decision is the authoritative clinical record, so once
 * it exists it wins here (DD-preferred). Before it exists — the whole point of
 * Path 1, where PAD is run and its report can be reviewed before DD — we fall
 * back to the provisional outcome so the PAD CAR renders the correct
 * positive/negative variant in the meantime. When the DD is later recorded/
 * changed, RD_DD re-renders the PAD CAR so its structure follows the formal DD.
 *
 * NOTE the deliberate asymmetry vs resolvePadAdhdOutcome (content): after a
 * mismatch, the rendered CAR *structure* follows DD while the drafted PAD
 * *narrative* stays provisional (v1 render-only policy). The residual tone gap
 * is surfaced by the PAD-DD mismatch guardrail, not auto-rewritten.
 *
 * @param {Object|null} patientDataModel
 * @returns {'positive'|'negative'}
 */
export const resolvePadReportStructureOutcome = (patientDataModel) => {
  const hasFormalDd = Boolean(
    patientDataModel?.dataModel?.['4_diagnosticDecision']?.lastCompletedAt
  );
  if (hasFormalDd) {
    return isADHDDiagnosisPositive(patientDataModel) ? 'positive' : 'negative';
  }
  const provisional = getPadProvisionalOutcome(patientDataModel);
  if (provisional) return provisional;
  return isADHDDiagnosisPositive(patientDataModel) ? 'positive' : 'negative';
};

/**
 * Reads the outcome the PAD was actually completed under, as stamped by
 * buildPadCompletionFields at true module completion. Returns null for a PAD that
 * has never been completed (or a legacy patient completed before this field
 * existed). See isPadStaleVsDd for how the null/legacy case is handled.
 *
 * @param {Object|null} patientDataModel
 * @returns {'positive'|'negative'|null}
 */
export const getPadCompletedOutcome = (patientDataModel) => {
  const outcome =
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.padCompletedOutcome;
  return outcome === 'positive' || outcome === 'negative' ? outcome : null;
};

/**
 * Guardrail: is a COMPLETED Post-Assessment Discussion now out of date because
 * the finalised Diagnostic Decision disagrees with the outcome the discussion was
 * actually held under?
 *
 * This generalises the original SLaM Path-1 provisional check: it primarily
 * compares the stamped `padCompletedOutcome` (the outcome the PAD was completed
 * under, in either Path) against the finalised formal DD outcome. It fires only
 * when ALL of these hold:
 *   - the PAD stage is `completed` (nothing to redo/flag while in progress — the
 *     mid-DD inline flag covers that case), and
 *   - the DD stage is `completed` (compare against the FINALISED decision only, so
 *     an edited-but-not-recompleted decision does not prematurely trip the cue), and
 *   - the completed PAD outcome differs from the formal DD outcome.
 *
 * Legacy fallback (no data migration): for patients whose PAD was completed before
 * `padCompletedOutcome` was introduced, fall back to the Path-1 provisional choice
 * so the cue is preserved for in-flight patients.
 *
 * NOTE: this is intentionally data-only. v1 scope limits the *cue and redo action*
 * to SLaM; that org gating is applied at the call sites (which have the workflow
 * config), not here.
 *
 * @param {Object|null} patientDataModel
 * @returns {boolean}
 */
export const isPadStaleVsDd = (patientDataModel) => {
  const padCompleted =
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.status === 'completed';
  if (!padCompleted) return false;

  const ddCompleted =
    patientDataModel?.dataModel?.['4_diagnosticDecision']?.status === 'completed';
  if (!ddCompleted) return false;

  const formalOutcome = isADHDDiagnosisPositive(patientDataModel) ? 'positive' : 'negative';

  const completedOutcome = getPadCompletedOutcome(patientDataModel);
  if (completedOutcome) {
    return completedOutcome !== formalOutcome;
  }

  // Legacy fallback for PADs completed before padCompletedOutcome existed.
  const provisional = getPadProvisionalOutcome(patientDataModel);
  if (provisional) {
    return provisional !== formalOutcome;
  }
  return false;
};

/**
 * Back-compat alias for the original SLaM Path-1 guardrail. Retained because
 * existing call sites (e.g. the Patient Overview PAD card) import this name.
 * Delegates to the generalised isPadStaleVsDd.
 *
 * @param {Object|null} patientDataModel
 * @returns {boolean}
 */
export const isPadOutOfDateWithDd = (patientDataModel) => isPadStaleVsDd(patientDataModel);

/**
 * Single source of truth for the PAD staleness cue used across the UI (Patient
 * Overview card, Documents dashboard row, and the DD-completion decision dialog).
 *
 * Returns both whether the completed PAD is now out of date versus the finalised
 * Diagnostic Decision AND the DIRECTION of the change, so callers can render
 * direction-aware copy without re-deriving outcomes:
 *   - fromOutcome: the outcome the discussion was actually held under
 *     (stamped padCompletedOutcome, else the Path-1 provisional choice).
 *   - toOutcome:   the finalised formal DD outcome.
 *   - direction:   'positiveToNegative' | 'negativeToPositive' (null when not stale).
 *
 * @param {Object|null} patientDataModel
 * @returns {{ isStale: boolean, direction: ('positiveToNegative'|'negativeToPositive'|null), fromOutcome: ('positive'|'negative'|null), toOutcome: ('positive'|'negative'|null) }}
 */
export const getPadStaleness = (patientDataModel) => {
  const isStale = isPadStaleVsDd(patientDataModel);
  if (!isStale) {
    return { isStale: false, direction: null, fromOutcome: null, toOutcome: null };
  }

  const toOutcome = isADHDDiagnosisPositive(patientDataModel) ? 'positive' : 'negative';
  // fromOutcome: prefer the stamped completion outcome; fall back to the Path-1
  // provisional for legacy PADs; finally infer the opposite of the DD outcome
  // (staleness guarantees they differ) so we always return a concrete direction.
  const fromOutcome =
    getPadCompletedOutcome(patientDataModel) ||
    getPadProvisionalOutcome(patientDataModel) ||
    (toOutcome === 'positive' ? 'negative' : 'positive');

  const direction =
    fromOutcome === 'positive' ? 'positiveToNegative' : 'negativeToPositive';

  return { isStale: true, direction, fromOutcome, toOutcome };
};

/**
 * Determines whether the patient's assessment follows the "ADHD path" — i.e.
 * whether SSI, DD, and PAD content should be treated as part of a full ADHD
 * assessment for report generation and required-section gating.
 *
 * The answer depends on the diagnostic model:
 *
 *  - Hierarchical orgs set a Provisional Primary Diagnosis (PPD) in the CI's
 *    Diagnostic Formulation.  PPD = ADHD means the patient is on the ADHD path.
 *    When PPD is unset (DF not yet completed), we default to ADHD-path (safe
 *    default — ensures no sections are accidentally skipped).
 *
 *  - Flat-model orgs have no PPD hierarchy; they list "Conditions in
 *    Consideration" instead.  For these patients the ADHD-path question is
 *    answered by the Diagnostic Decision outcome.  Before DD is completed
 *    isADHDDiagnosisPositive returns false, but that is safe because auto-
 *    report generation only runs after DD approval.
 *
 * This helper keeps the flat-vs-hierarchical branching in one place so that
 * reportTypeUtils, autoPatientReport, and autoCoverPage stay consistent.
 *
 * @param {Object} patientDataModel - The patient's Firestore document
 * @param {Object} wf - Resolved workflow config (from resolveWorkflowConfig)
 * @returns {boolean}
 */
export const isAdhdAssessmentFlow = (patientDataModel, wf) => {
  if (wf?.isFlatDifferentials) {
    return isADHDDiagnosisPositive(patientDataModel);
  }
  return isADHDProvisionalPrimaryDiagnosis(patientDataModel);
};

/**
 * Determines if the SSI recommended ADHD diagnosis as positive (Fully met or Partially met)
 * Used for conflict detection in 3 Borough / SLaM workflow where SSI is done before CI.
 * 
 * @param {Object} patientDataModel - The patient data model
 * @returns {boolean} - True if SSI recommended positive ADHD (Fully met or Partially met), false otherwise
 */
export const isSSIRecommendationPositive = (patientDataModel) => {
  // Early return if no data model
  if (!patientDataModel?.dataModel) return false;

  const SSI_MODULE = '3_semiStructuredInterview';
  const RDD_SECTION_NUMBER = '3';  // Recommended Diagnosis Decision section
  const POSITIVE_DECISIONS = ['Fully met', 'Partially met'];

  try {
      // Get the RDD section directly by section number
      const rddSection = patientDataModel?.dataModel?.[SSI_MODULE]?.sections?.[RDD_SECTION_NUMBER];
      
      if (!rddSection) return false;

      // Get the diagnosis decision from the RDD section
      const diagnosisDecision = rddSection?.diagnosisDecision;

      // Return true if recommendation is Fully met or Partially met
      return POSITIVE_DECISIONS.includes(diagnosisDecision);
  } catch (error) {
      console.error('Error checking SSI recommendation:', error);
      return false; // Default to false on error
  }
};

/**
 * Gets the SSI recommendation details for conflict detection
 * Returns the full recommendation including decision, presentation, and severity
 * 
 * @param {Object} patientDataModel - The patient data model
 * @returns {Object} - SSI recommendation details or null if not available
 */
export const getSSIRecommendationDetails = (patientDataModel) => {
  if (!patientDataModel?.dataModel) return null;

  const SSI_MODULE = '3_semiStructuredInterview';
  const RDD_SECTION_NUMBER = '3';

  try {
      const rddSection = patientDataModel?.dataModel?.[SSI_MODULE]?.sections?.[RDD_SECTION_NUMBER];
      
      if (!rddSection?.diagnosisDecision) return null;

      return {
          diagnosisDecision: rddSection.diagnosisDecision,
          diagnosisPresentation: rddSection.diagnosisPresentation || null,
          diagnosisSeverity: rddSection.diagnosisSeverity || null,
          partialDiagnosisClassification: rddSection.partialDiagnosisClassification || null,
          // DSM criteria for detailed conflict info
          dsmCriterionA_SymptomThreshold_A: rddSection.dsmCriterionA_SymptomThreshold_A,
          dsmCriterionA_SymptomThreshold_HI: rddSection.dsmCriterionA_SymptomThreshold_HI,
          dsmCriterionB_AgeOfOnset: rddSection.dsmCriterionB_AgeOfOnset,
          dsmCriterionC_SymptomsAcrossSettings: rddSection.dsmCriterionC_SymptomsAcrossSettings,
          dsmCriterionD_FunctionalImpairment: rddSection.dsmCriterionD_FunctionalImpairment,
          dsmCriterionE_NotOtherExplanation: rddSection.dsmCriterionE_NotOtherExplanation,
      };
  } catch (error) {
      console.error('Error getting SSI recommendation details:', error);
      return null;
  }
};

/**
 * Gets the provisional primary diagnosis (PPD) from the Clinical Interview
 * 
 * @param {Object} patientDataModel - The patient data model
 * @returns {string|null} - The PPD value or null if not available
 */
export const getProvisionalPrimaryDiagnosis = (patientDataModel) => {
  if (!patientDataModel?.dataModel) return null;

  const CLINICAL_INTERVIEW_MODULE = '2_clinicalInterview';
  const DIAGNOSTIC_FORMULATION_VAR_NAME = 'diagnosticFormulation';

  try {
      // Find the Diagnostic Formulation section by its sectionVarName
      const diagnosticFormulationSection = Object.values(patientDataModel?.dataModel?.[CLINICAL_INTERVIEW_MODULE]?.sections || {})
          .find(section => section?.sectionVarName === DIAGNOSTIC_FORMULATION_VAR_NAME);
      
      return diagnosticFormulationSection?.provisionalPrimaryDiagnosis || null;
  } catch (error) {
      console.error('Error getting provisional primary diagnosis:', error);
      return null;
  }
};

// ---------------------------------------------------------------------------
// Criterion E prefill helper
// ---------------------------------------------------------------------------

const ADHD_CONDITION = 'Attention Deficit Hyperactivity Disorder (ADHD)';

/**
 * Compute the Criterion E ("Exclusion of Other Disorders") prefill value
 * and a human-readable reason string for display in the UI banner.
 *
 * The prefill depends on:
 *   - Whether the org uses multi-step DD (SLaM, TOC) — never auto-prefill;
 *     the clinician must explicitly select Yes/No before confirming Decision
 *     for ADHD (same hard gate as presentation / severity)
 *   - Whether the DF has been completed yet (deferred DF / SSI-first timing)
 *   - Whether the org uses a hierarchical or flat DF model
 *   - For hierarchical: whether ADHD is the Provisional Primary Diagnosis
 *   - For flat: whether ADHD is among the Conditions Under Consideration / Emerging Differentials
 *
 * When an RDD value already exists (clinician has explicitly decided in the
 * Recommended Diagnosis Decision), DD pages can pass it in to carry it over
 * rather than recomputing from scratch. Multi-step DD orgs (SLaM / TOC) do
 * not use RDD, so that path is unused for them.
 *
 * @param {Object} wf - Resolved workflow config (from useWorkflowConfig)
 * @param {Object|null} patientDataModel - The patient's Firestore document
 * @param {Object} [options]
 * @param {boolean|null} [options.rddValue] - Existing RDD Criterion E value
 *   to carry over (undefined = not available / not applicable)
 * @returns {{ value: boolean|null, reason: string }}
 */
export const computeCriterionEPrefill = (wf, patientDataModel, options = {}) => {
  const { rddValue } = options;

  // If an RDD value was explicitly provided (clinician already decided in
  // the Recommended Diagnosis Decision), carry it over as-is.
  if (rddValue !== undefined && rddValue !== null) {
    return {
      value: rddValue,
      reason: 'Carried over from Recommended Diagnosis Decision after Semi-Structured Interview.',
      cta: 'Please update as appropriate.',
    };
  }

  // Multi-step DD (SLaM, TOC, …): never auto-select Criterion E from the DF.
  // Empty differentials / no other conditions used to prefill "No", and a
  // positive ADHD pathway / PPD used to prefill "Yes" — both skipped the
  // clinician's required judgement at Decision for ADHD. Leave blank and
  // require an explicit Yes/No (enforced by isFormValid on 1_DD).
  if (wf?.hasMultiStepDD) {
    return {
      value: null,
      reason:
        'Criterion E is not pre-selected. Please use your clinical judgement to decide whether the symptoms are better explained by another psychiatric disorder, substance, or medication.',
      cta: 'A selection is required before you can confirm the Decision for ADHD.',
    };
  }

  // DF section data lives at 2_clinicalInterview.sections.12 regardless of
  // whether the DF page is in the CI or deferred to end of SSI.
  const dfSection = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.['12']
    || patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[12]
    || {};

  const dfCompleted = dfSection.status === 'completed';

  // -- Deferred DF: DF hasn't happened yet at the time of RDD --
  if (wf?.isDFDeferred && !dfCompleted) {
    return {
      value: null,
      reason: 'Criterion E is not pre-selected because the Diagnostic Formulation has not yet been completed.',
    };
  }

  // -- SSI-first (e.g. SLaM): CI happens after SSI, so at RDD time the
  //    CI DF may not exist yet --
  if (wf?.isSSIFirst && !dfCompleted) {
    return {
      value: null,
      reason: 'Criterion E is not pre-selected because the Clinical Interview has not yet been completed.',
      cta: 'This can be left blank and will be automatically updated after the Diagnostic Formulation has been done.',
    };
  }

  // -- DF is completed (or CI-first non-deferred where DF precedes RDD) --

  if (wf?.isFlatDifferentials) {
    // Flat DF: check whether ADHD is in the conditions under consideration.
    // All flat-DF orgs (TOC, WLC, etc.) use the same unified label.
    const differentials = dfSection.differentialDiagnoses || [];
    const adhdIncluded = differentials.some(
      d => d?.toLowerCase() === ADHD_CONDITION.toLowerCase()
    );

    if (!adhdIncluded) {
      return {
        value: false,
        reason: "Pre-selected as 'No' because ADHD was not included in the conditions under consideration.",
      };
    }

    // ADHD is in the list, but inclusion alone doesn't confirm it as the
    // best explanation — the clinician must decide.
    return {
      value: null,
      reason: 'ADHD is in the conditions under consideration. Please use your clinical judgement to determine if ADHD best explains the symptoms.',
    };
  }

  // -- Hierarchical DF: use the Provisional Primary Diagnosis --
  const isAdhdPPD = isADHDProvisionalPrimaryDiagnosis(patientDataModel);

  if (isAdhdPPD) {
    return {
      value: true,
      reason: 'Pre-selected based on ADHD being the Provisional Primary Diagnosis in your Diagnostic Formulation.',
    };
  }

  return {
    value: false,
    reason: 'Pre-selected as \'No\' because ADHD was not identified as the primary condition in the Diagnostic Formulation.',
  };
};

/**
 * Determines if the patient is female based on their form data
 * @param {Object} data - Either the patientDataModel or the formData directly
 * @returns {boolean} - True if patient is female, false otherwise
 */
/**
 * Returns the patient's sex assigned at birth as recorded on their intake form,
 * or null if the answer is unavailable (e.g. not yet answered or data missing).
 *
 * @param {Object} data - Either the patientDataModel or the formData directly
 * @returns {string|null} - e.g. "Female", "Male", "Intersex", "Prefer not to say", or null
 */
export const getPatientSexAtBirth = (data) => {
  if (!data) return null;

  const formData = data.formData || data;
  if (!formData) return null;

  try {
    const yourInformationSection = formData['yourInformation'];
    if (!yourInformationSection?.formSubsections) return null;

    const subsection = Object.values(yourInformationSection.formSubsections)
      .find(sub =>
        Object.values(sub.questions || {})
          .some(q => q.questionVarName === 'sexAssignedAtBirth')
      );
    if (!subsection?.questions) return null;

    const genderQuestion = Object.values(subsection.questions)
      .find(q => q.questionVarName === 'sexAssignedAtBirth');
    if (!genderQuestion?.subquestions) return null;

    const genderSubquestion = Object.values(genderQuestion.subquestions)
      .find(sq => sq.serviceUserVarName === 'sexAssignedAtBirth');

    return genderSubquestion?.answer?.value || null;
  } catch (error) {
    console.error('Error reading patient sex at birth:', error);
    return null;
  }
};

export const isPatientFemale = (data) => {
  if (!data) return false;
  
  // Determine if we got the full patientDataModel or just the formData
  const formData = data.formData || data;
  if (!formData) return false;

  const GENDER_QUESTION_PATH = {
      section: 'yourInformation',
      questionVarName: 'sexAssignedAtBirth',
      serviceUserVarName: 'sexAssignedAtBirth'
  };

  const GENDER_VALUE = 'Female';

  try {
      // Find the question by its varName
      const yourInformationSection = formData[GENDER_QUESTION_PATH.section];
      if (!yourInformationSection?.formSubsections) return false;

      // Find the subsection containing the gender question
      const subsection = Object.values(yourInformationSection.formSubsections)
          .find(subsection => 
              Object.values(subsection.questions || {})
                  .some(question => question.questionVarName === GENDER_QUESTION_PATH.questionVarName)
          );

      if (!subsection?.questions) return false;

      // Find the gender question
      const genderQuestion = Object.values(subsection.questions)
          .find(question => question.questionVarName === GENDER_QUESTION_PATH.questionVarName);

      if (!genderQuestion?.subquestions) return false;

      // Find the gender subquestion
      const genderSubquestion = Object.values(genderQuestion.subquestions)
          .find(subquestion => subquestion.serviceUserVarName === GENDER_QUESTION_PATH.serviceUserVarName);

      const gender = genderSubquestion?.answer?.value;
      return gender === GENDER_VALUE;
  } catch (error) {
      console.error('Error checking patient gender:', error);
      return false; // Default to false on error
  }
};


/**
 * Determine if an Autism patient is female, using the history stage data.
 *
 * In the Adult Autism assessment, the patient's sex is captured in the
 * history stage document at section 1 → subsection 1 → question 3
 * (providerVarName: "sexAssignedAtBirth"). The value is stored in the
 * patientPrefill field as a plain string answer (e.g. "Female", "Male").
 *
 * @param {Object} assessmentStages  - The full assessmentStages map from
 *                                     ModularPatientDataContext.
 * @returns {boolean}  True if the patient's reported sex is "Female".
 */
export const isAutismPatientFemale = (assessmentStages) => {
  if (!assessmentStages) return false;

  try {
    const historyData = assessmentStages?.history?.data;
    if (!historyData) return false;

    const question = historyData?.sections?.['1']?.subsections?.['1']
      ?.subsectionQuestions?.['3'];
    if (!question) return false;

    const prefill = question.patientPrefill;
    if (typeof prefill === 'string') {
      return prefill.trim().toLowerCase() === 'female';
    }
    if (prefill && typeof prefill === 'object') {
      const val = prefill.answer?.value || prefill.value || '';
      return val.trim().toLowerCase() === 'female';
    }
    return false;
  } catch (error) {
    console.error('Error checking Autism patient gender:', error);
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// CYP ADHD: sex assigned at birth and the "periods gate"
//
// The CYP pathway asks the parent/carer about the child's periods, shows a
// "Puberty" panel on the Clinical Interview and drafts a Puberty paragraph.
// None of that applies when the child's sex assigned at birth is Male or
// Intersex. These helpers are the single place the frontend decides that,
// mirroring python-functions/constants/cyp_adhd/sex_at_birth.py exactly.
// If you change the option list or the hide set here, change it there too.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The parent/carer option list, identical to Adult ADHD's sexAssignedAtBirth
 * question so both pathways record the same vocabulary.
 */
export const CYP_SEX_AT_BIRTH_OPTIONS = ['Female', 'Male', 'Intersex', 'Prefer not to say'];

/**
 * Lower-cased values that switch the periods content OFF. Intersex is in
 * this set by product decision (28 Oct 2026) and is flagged for clinical
 * review, since some intersex children do menstruate.
 */
export const CYP_SEX_AT_BIRTH_HIDES_PERIODS = ['male', 'intersex'];

/**
 * Read the child's sex assigned at birth from a CYP ADHD patient header.
 *
 * The canonical copy lives at `patients/{pid}.sexAssignedAtBirth`, written by
 * patient setup and overwritten by the parent's confirmed answer. The
 * clinician edit form also mirrors it into `customFields`, so that is used as
 * a fallback for records where only the custom field was set.
 *
 * @param {Object} header - The patient header document (ModularPatientDataContext.header)
 * @returns {string|null} The raw stored value, or null when nothing is recorded
 */
export const getCypPatientSexAtBirth = (header) => {
  if (!header) return null;
  const direct = header.sexAssignedAtBirth;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  const custom = header.customFields?.sexAssignedAtBirth;
  if (typeof custom === 'string' && custom.trim()) return custom.trim();
  return null;
};

/**
 * Should the periods questions, CI "Puberty" panel and paragraph be shown?
 *
 * The rule is deliberately fail-open: hide ONLY when the recorded sex is
 * explicitly Male or Intersex. Female, "Prefer not to say", no value at all,
 * or something we do not recognise all return true. A hidden question is
 * invisible to the clinician, while an unnecessary question is a small
 * annoyance the parent can answer with "Not applicable", so "we do not know"
 * must never hide clinical content.
 *
 * @param {string|null|undefined} value - The stored sex-at-birth value
 * @returns {boolean} true to show the periods content, false to hide it
 */
export const cypPeriodsContentApplies = (value) => {
  if (typeof value !== 'string') return true;
  const normalised = value.trim().toLowerCase();
  if (!normalised) return true;
  return !CYP_SEX_AT_BIRTH_HIDES_PERIODS.includes(normalised);
};